(() => {
  const hydrateDeferredMedia = (root) => {
    const media = [];
    if (root?.matches?.('[data-navo-deferred-media]')) media.push(root);
    root?.querySelectorAll?.('[data-navo-deferred-media]')?.forEach((item) => media.push(item));

    media.forEach((item) => {
      if (item.dataset.src) item.src = item.dataset.src;
      if (item.dataset.srcset) item.srcset = item.dataset.srcset;
      item.removeAttribute('data-src');
      item.removeAttribute('data-srcset');
      item.removeAttribute('data-navo-deferred-media');
    });
  };

  const initDeferredMedia = (root = document) => {
    const media = [...root.querySelectorAll('[data-navo-deferred-media]')];
    if (!media.length) return;

    if (!('IntersectionObserver' in window)) {
      media.forEach((item) => hydrateDeferredMedia(item));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        hydrateDeferredMedia(entry.target);
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '400px 0px' });

    media.forEach((item) => observer.observe(item));
  };

  const initCarousels = (root = document) => {
    root.querySelectorAll('[data-navo-carousel]:not([data-ready])').forEach((carousel) => {
      const track = carousel.querySelector('[data-carousel-track]');
      const slides = [...carousel.querySelectorAll('[data-carousel-slide]')];
      const dots = carousel.querySelector('[data-carousel-dots]');
      if (!track || !slides.length || !dots) return;

      carousel.dataset.ready = 'true';
      let active = 0;
      const itemLabel = carousel.dataset.carouselItemLabel || 'imagem';
      const swapSlides = carousel.classList.contains('n1-carousel--product');

      const render = () => {
        if (swapSlides) hydrateDeferredMedia(slides[active]);
        slides[active]?.querySelectorAll('img').forEach((image) => { image.loading = 'eager'; });
        if (swapSlides) {
          track.style.transform = 'none';
          slides.forEach((slide, index) => {
            const isActive = index === active;
            slide.hidden = !isActive;
            slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
          });
        } else {
          track.style.transform = `translateX(-${active * 100}%)`;
        }
        [...dots.children].forEach((dot, index) => {
          dot.setAttribute('aria-current', index === active ? 'true' : 'false');
        });
      };

      const selectSlide = async (index, source = 'unknown') => {
        const next = (index + slides.length) % slides.length;
        if (next === active) return;
        hydrateDeferredMedia(slides[next]);
        const image = slides[next]?.querySelector('img');
        if (image) {
          image.loading = 'eager';
          if (!image.complete) {
            await Promise.race([
              new Promise((resolve) => {
                image.addEventListener('load', resolve, { once: true });
                image.addEventListener('error', resolve, { once: true });
              }),
              new Promise((resolve) => window.setTimeout(resolve, 4000))
            ]);
          }
          if (typeof image.decode === 'function') {
            try { await image.decode(); } catch {}
          }
        }
        active = next;
        render();
        carousel.dispatchEvent(new CustomEvent('navo:carousel-change', {
          bubbles: true,
          detail: { index: active, source }
        }));
      };

      slides.forEach((_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.setAttribute('aria-label', `Mostrar ${itemLabel} ${index + 1}`);
        dot.addEventListener('click', () => { void selectSlide(index, 'dot'); });
        dots.appendChild(dot);
      });

      carousel.querySelector('[data-carousel-prev]')?.addEventListener('click', () => {
        void selectSlide(active - 1, 'previous');
      });
      carousel.querySelector('[data-carousel-next]')?.addEventListener('click', () => {
        void selectSlide(active + 1, 'next');
      });

      let touchStart = 0;
      carousel.addEventListener('touchstart', (event) => { touchStart = event.touches[0].clientX; }, { passive: true });
      carousel.addEventListener('touchend', (event) => {
        const delta = event.changedTouches[0].clientX - touchStart;
        if (Math.abs(delta) < 42) return;
        void selectSlide(delta < 0 ? active + 1 : active - 1, 'swipe');
      }, { passive: true });

      render();
    });
  };

  const initPurchaseOptions = (root = document) => {
    root.querySelectorAll('[data-navo-plans]:not([data-ready])').forEach((group) => {
      const options = [...group.querySelectorAll('[data-navo-plan]')];
      if (!options.length) return;
      group.dataset.ready = 'true';
      const section = group.closest('.n1-product-hero');
      const form = section?.querySelector('[data-navo-purchase-form]');
      const variantInput = form?.querySelector('[data-navo-purchase-variant]');
      const submit = form?.querySelector('[data-navo-purchase-submit]');
      const submitLabel = form?.querySelector('[data-navo-purchase-label]');
      const status = form?.querySelector('[data-navo-purchase-status]');
      const stickyPurchase = section?.querySelector('[data-navo-sticky-purchase]');
      const stickyDuration = stickyPurchase?.querySelector('[data-navo-sticky-duration]');
      const stickyPrice = stickyPurchase?.querySelector('[data-navo-sticky-price]');
      const stickySubmit = stickyPurchase?.querySelector('[data-navo-sticky-submit]');
      const initialLabel = submitLabel?.textContent || 'Selecione uma opção';
      const initialStatus = status?.textContent || '';

      const selectOption = (selected) => {
        options.forEach((option) => {
          const isSelected = option === selected;
          option.classList.toggle('is-selected', isSelected);
          option.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        });

        if (!form || !variantInput || !submit || !submitLabel || !status) return;
        const available = selected.dataset.planAvailable === 'true' && Boolean(selected.dataset.planVariantId);
        variantInput.value = available ? selected.dataset.planVariantId : '';
        submit.disabled = !available;
        submitLabel.textContent = available
          ? (selected.dataset.planButtonLabel || `Comprar ${selected.dataset.planTitle || 'esta opção'}`)
          : initialLabel;
        if (stickyDuration) stickyDuration.textContent = selected.dataset.planDuration || selected.dataset.planTitle || '';
        if (stickyPrice) stickyPrice.textContent = selected.dataset.planPrice || '';
        if (stickySubmit) stickySubmit.disabled = !available;
        status.textContent = available
          ? `${selected.dataset.planTitle || 'Opção selecionada'} · ${selected.dataset.planPrice || ''}`
          : 'Esta opção ainda não está disponível para compra.';
        form.dataset.selectedPlan = selected.dataset.planTitle || '';
        form.dataset.selectedDuration = selected.dataset.planDuration || '';
        form.dataset.selectedPrice = selected.dataset.planPrice || '';
        form.dataset.selectedProductHandle = selected.dataset.planProductHandle || '';
      };

      options.forEach((option) => {
        option.addEventListener('click', () => selectOption(option));
        option.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          selectOption(option);
        });
      });

      form?.addEventListener('reset', () => {
        options.forEach((option) => {
          option.classList.remove('is-selected');
          option.setAttribute('aria-checked', 'false');
        });
        if (variantInput) variantInput.value = '';
        if (submit) submit.disabled = true;
        if (stickySubmit) stickySubmit.disabled = true;
        if (submitLabel) submitLabel.textContent = initialLabel;
        if (status) status.textContent = initialStatus;
      });

      form?.addEventListener('submit', async (event) => {
        const variantId = variantInput?.value || '';
        if (!variantId || !submit || !status) return;
        event.preventDefault();
        if (form.dataset.submitting === 'true') return;

        form.dataset.submitting = 'true';
        submit.disabled = true;
        submit.setAttribute('aria-busy', 'true');
        if (stickySubmit) {
          stickySubmit.disabled = true;
          stickySubmit.setAttribute('aria-busy', 'true');
        }
        status.textContent = 'A adicionar ao carrinho…';

        try {
          const rootUrl = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${rootUrl}cart/add.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({
              items: [{ id: Number(variantId), quantity: 1 }]
            })
          });
          if (!response.ok) throw new Error(`Cart add failed with status ${response.status}`);
          await response.json();
          await new Promise((resolve) => window.setTimeout(resolve, 250));
          window.location.assign(`${rootUrl}cart`);
        } catch (error) {
          console.error('[NAVO1] Não foi possível adicionar ao carrinho.', error);
          form.dataset.submitting = 'false';
          submit.disabled = false;
          submit.removeAttribute('aria-busy');
          if (stickySubmit) {
            stickySubmit.disabled = false;
            stickySubmit.removeAttribute('aria-busy');
          }
          status.textContent = 'Não foi possível adicionar. Tente novamente.';
        }
      });

      if (form && stickyPurchase && 'IntersectionObserver' in window) {
        const observer = new IntersectionObserver(([entry]) => {
          stickyPurchase.classList.toggle('is-main-visible', entry.isIntersecting);
        }, { threshold: 0.15 });
        observer.observe(form);
      }
    });
  };

  const initProgressJourneys = (root = document) => {
    root.querySelectorAll('[data-navo-progress]:not([data-progress-ready])').forEach((journey) => {
      const steps = [...journey.querySelectorAll('[data-progress-step]')];
      if (!steps.length) return;
      journey.dataset.progressReady = 'true';
      let lastAutomaticOpen = 0;

      const openStep = (step, source = 'scroll') => {
        if (!step || step.classList.contains('is-open')) return;
        const now = window.performance.now();
        if (source === 'scroll' && now - lastAutomaticOpen < 900) return;
        step.classList.add('is-open');
        if (source === 'scroll') lastAutomaticOpen = now;
        step.querySelector('[data-progress-trigger]')?.setAttribute('aria-expanded', 'true');
        step.querySelector('[data-progress-panel]')?.setAttribute('aria-hidden', 'false');
        journey.dispatchEvent(new CustomEvent('navo:progress-open', {
          bubbles: true,
          detail: { week: step.dataset.week, source }
        }));
      };

      steps.forEach((step) => {
        step.querySelector('[data-progress-trigger]')?.addEventListener('click', () => openStep(step, 'click'));
      });

      let scheduled = false;
      const revealNext = () => {
        scheduled = false;
        const next = steps.find((step) => !step.classList.contains('is-open'));
        if (!next) {
          window.removeEventListener('scroll', requestReveal);
          return;
        }
        const rect = next.getBoundingClientRect();
        const triggerLine = window.innerHeight * 0.72;
        if (rect.top <= triggerLine && rect.bottom >= window.innerHeight * 0.12) openStep(next);
      };
      const requestReveal = () => {
        if (scheduled) return;
        scheduled = true;
        window.requestAnimationFrame(revealNext);
      };

      window.addEventListener('scroll', requestReveal, { passive: true });
      requestReveal();
    });
  };

  const initAutoplayVideos = (root = document) => {
    root.querySelectorAll('[data-navo-autoplay-video]:not([data-video-ready])').forEach((wrapper) => {
      const video = wrapper.querySelector('video');
      const toggle = wrapper.querySelector('[data-video-toggle]');
      if (!video || !toggle) return;
      wrapper.dataset.videoReady = 'true';
      wrapper.dataset.userPaused = 'false';
      video.muted = true;
      video.defaultMuted = true;

      const hydrateVideo = () => {
        if (video.dataset.hydrated === 'true') return;
        if (video.dataset.poster) {
          video.poster = video.dataset.poster;
          video.removeAttribute('data-poster');
        }
        video.querySelectorAll('source[data-src]').forEach((source) => {
          source.src = source.dataset.src;
          source.removeAttribute('data-src');
        });
        video.dataset.hydrated = 'true';
        video.preload = 'auto';
        video.load();
      };

      const updateToggle = () => {
        const userPaused = wrapper.dataset.userPaused === 'true';
        toggle.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
        toggle.setAttribute('aria-label', userPaused ? 'Reproduzir vídeo' : 'Pausar vídeo');
      };
      const play = () => {
        if (wrapper.dataset.userPaused === 'true' || document.hidden) return;
        hydrateVideo();
        if (video.readyState === 0) video.load();
        const promise = video.play();
        if (promise?.catch) promise.catch(() => {});
      };

      toggle.addEventListener('click', () => {
        const shouldPause = wrapper.dataset.userPaused !== 'true';
        wrapper.dataset.userPaused = shouldPause ? 'true' : 'false';
        if (shouldPause) video.pause();
        else play();
        updateToggle();
      });

      if ('IntersectionObserver' in window) {
        const loadObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            hydrateVideo();
            loadObserver.unobserve(entry.target);
          });
        }, { rootMargin: '500px 0px' });
        loadObserver.observe(wrapper);

        const playObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.25) play();
            else video.pause();
          });
        }, { threshold: [0, 0.25] });
        playObserver.observe(wrapper);
      } else {
        play();
      }

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) video.pause();
        else if (wrapper.getBoundingClientRect().bottom > 0 && wrapper.getBoundingClientRect().top < window.innerHeight) play();
      });
      updateToggle();
    });
  };

  const initScrollCtas = (root = document) => {
    root.querySelectorAll('[data-navo-scroll-cta]:not([data-scroll-cta-ready])').forEach((cta) => {
      cta.dataset.scrollCtaReady = 'true';
      cta.addEventListener('click', (event) => {
        const targetLink = cta.getAttribute('href');
        if (targetLink?.startsWith('#')) {
          const target = document.querySelector(targetLink);
          if (target) {
            event.preventDefault();
            window.history.replaceState(null, '', targetLink);
            target.scrollIntoView({
              behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
              block: 'start'
            });
          }
        }
      });
    });
  };

  const initPage = () => {
    initDeferredMedia();
    initCarousels();
    initPurchaseOptions();
    initProgressJourneys();
    initAutoplayVideos();
    initScrollCtas();
  };

  initPage();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPage, { once: true });
  }
  document.addEventListener('shopify:section:load', (event) => {
    initDeferredMedia(event.target);
    initCarousels(event.target);
    initPurchaseOptions(event.target);
    initProgressJourneys(event.target);
    initAutoplayVideos(event.target);
    initScrollCtas(event.target);
  });
})();
